
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import {Video} from '@google/genai';
import React, {useCallback, useEffect, useState} from 'react';
import ApiKeyDialog from './components/ApiKeyDialog';
import BrandKit from './components/BrandKit';
import {CurvedArrowDownIcon, KeyIcon} from './components/icons';
import LoadingIndicator from './components/LoadingIndicator';
import PromptForm from './components/PromptForm';
import VideoResult from './components/VideoResult';
import {generateVideo} from './services/geminiService';
import {
  AppState,
  AspectRatio,
  BrandIdentity,
  GenerateVideoParams,
  GenerationMode,
  ImageFile,
  Resolution,
  VideoFile,
} from './types';

const INITIAL_BRAND_CONFIG: BrandIdentity = {
  systemInstruction: "Persona: A charismatic, physically attractive DIY furniture designer and maker influencer. Exudes competence and confidence. Dressed in stylish, fitted workwear. MANDATORY: Maintain strict temporal coherence and visual vocal consistency across the entire sequence. The character's facial expressions, mouth movements, and micro-gestures must remain consistent with previous frames. Visual identity of CutCalc.xyz: Burnt Orange (#D87431) for primary actions, White backgrounds (#FFFFFF), and a muted pastel palette for cut layouts.",
  prefix: "High quality social media content, cinematic vertical video. Interior of a beautifully lit, organized, professional woodshop or artist studio. Focus on the attractive influencer main character working on a high-end furniture build (specified by user prompt). The character pauses to consult their device, using the CutCalc.xyz app to optimize their workflow... ",
  suffix: " ...Shot on 35mm film stock emulation, 4K resolution, anamorphic lens with subtle horizontal flares, high fashion lighting (rembrandt key light on face, rim light on shoulders/hair), shallow depth of field (bokeh background). The overall look is 'premium maker aesthetic'.",
  negativeConstraints: "Forbidden: Vocal changes, erratic mouth movements, non-logical tool operations, character drift (changing facial features or body type), project drift (furniture piece changing shape mid-video), setting drift (background workshop changing layout), low resolution UI, neon or cyber-punk UI colors.",
  referenceImages: [],
  isActive: true,
};

const extractFirstFrame = (videoUrl: string): Promise<ImageFile> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.currentTime = 0;
    video.onloadeddata = () => {
      setTimeout(() => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], 'sequence_start.png', { type: 'image/png' });
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = (reader.result as string).split(',')[1];
                resolve({ file, base64 });
              };
              reader.readAsDataURL(file);
            } else {
              reject(new Error('Canvas blob failed'));
            }
          }, 'image/png');
        }
      }, 100);
    };
    video.onerror = (e) => reject(e);
  });
};

const App: React.FC = () => {
  const [appState, setAppState] = useState<AppState>(AppState.IDLE);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [parentVideoUrl, setParentVideoUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastConfig, setLastConfig] = useState<GenerateVideoParams | null>(null);
  const [lastVideoObject, setLastVideoObject] = useState<Video | null>(null);
  const [lastVideoBlob, setLastVideoBlob] = useState<Blob | null>(null);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<GenerateVideoParams | null>(null);
  const [isBrandKitOpen, setIsBrandKitOpen] = useState(false);
  const [brandConfig, setBrandConfig] = useState<BrandIdentity>(INITIAL_BRAND_CONFIG);
  const [sequenceStartFrame, setSequenceStartFrame] = useState<ImageFile | null>(null);

  const checkApiKeyStatus = useCallback(async () => {
    // Check if the environment already has a key (e.g. set via Vite or hosting)
    if (process.env.API_KEY && process.env.API_KEY !== '' && process.env.API_KEY !== 'undefined') {
      setHasApiKey(true);
      return true;
    }

    // Otherwise check the platform specific aistudio selector
    if (window.aistudio) {
      try {
        const hasKey = await window.aistudio.hasSelectedApiKey();
        setHasApiKey(hasKey);
        return hasKey;
      } catch (error) {
        console.warn('aistudio check failed', error);
        return false;
      }
    }
    return false;
  }, []);

  useEffect(() => {
    const init = async () => {
      const hasKey = await checkApiKeyStatus();
      if (!hasKey) setShowApiKeyDialog(true);
    };
    init();
  }, [checkApiKeyStatus]);

  const handleOpenKeySelector = async () => {
    if (window.aistudio) {
      await window.aistudio.openSelectKey();
      // Assume success after opening dialog as per race condition rules
      setHasApiKey(true);
      setShowApiKeyDialog(false);
    } else {
      // If no platform helper, just try to continue (checking env again)
      const hasKey = await checkApiKeyStatus();
      if (hasKey) {
        setShowApiKeyDialog(false);
      } else {
        setErrorMessage("No API key found. Please set your API_KEY environment variable or run this app in Google AI Studio.");
      }
    }
  };

  const handleGenerate = useCallback(async (params: GenerateVideoParams) => {
    setAppState(AppState.LOADING);
    setErrorMessage(null);
    
    const paramsWithCoreBrand = { ...params, brandIdentity: brandConfig };
    setLastConfig(paramsWithCoreBrand);
    setInitialFormValues(null);

    try {
      const {objectUrl, blob, video} = await generateVideo(paramsWithCoreBrand);
      
      if (params.mode !== GenerationMode.EXTEND_VIDEO) {
        try {
          const firstFrame = await extractFirstFrame(objectUrl);
          setSequenceStartFrame(firstFrame);
        } catch (e) {
          console.warn("Could not extract sequence start frame", e);
        }
      }

      setVideoUrl(objectUrl);
      setLastVideoBlob(blob);
      setLastVideoObject(video);
      setAppState(AppState.SUCCESS);
    } catch (error: any) {
      console.error('Video generation failed:', error);
      const msg = error instanceof Error ? error.message : 'An unknown error occurred.';
      
      // If project/entity not found, user might need to re-select key
      if (msg.includes("Requested entity was not found") || msg.includes("API key not valid")) {
        setHasApiKey(false);
        setShowApiKeyDialog(true);
      }
      
      setErrorMessage(msg);
      setAppState(AppState.ERROR);
    }
  }, [brandConfig]);

  const handleRetry = useCallback(() => {
    if (lastConfig) handleGenerate(lastConfig);
  }, [lastConfig, handleGenerate]);

  const handleNewVideo = useCallback(() => {
    setAppState(AppState.IDLE);
    setVideoUrl(null);
    setParentVideoUrl(null);
    setErrorMessage(null);
    setLastConfig(null);
    setLastVideoObject(null);
    setLastVideoBlob(null);
    setInitialFormValues(null);
    setSequenceStartFrame(null);
  }, []);

  const handleExtend = useCallback(async () => {
    if (lastConfig && lastVideoBlob && lastVideoObject) {
      const file = new File([lastVideoBlob], 'last_video.mp4', {type: lastVideoBlob.type});
      const videoFile: VideoFile = {file, base64: ''};
      setParentVideoUrl(videoUrl);
      
      setInitialFormValues({
        ...lastConfig,
        mode: GenerationMode.EXTEND_VIDEO,
        prompt: '', 
        inputVideo: videoFile, 
        inputVideoObject: lastVideoObject, 
        resolution: Resolution.P720,
        brandIdentity: brandConfig,
        loopBackFrame: sequenceStartFrame,
        referenceImages: lastConfig.referenceImages || [],
      });
      setAppState(AppState.IDLE);
      setVideoUrl(null);
    }
  }, [lastConfig, lastVideoBlob, lastVideoObject, videoUrl, sequenceStartFrame, brandConfig]);

  const isQuotaError = errorMessage?.toLowerCase().includes('quota') || errorMessage?.toLowerCase().includes('rate limit') || errorMessage?.toLowerCase().includes('exhausted');

  return (
    <div className="h-screen bg-black text-gray-200 flex flex-col font-sans overflow-hidden">
      {showApiKeyDialog && <ApiKeyDialog onContinue={handleOpenKeySelector} />}
      
      <BrandKit 
        config={brandConfig} 
        onChange={setBrandConfig} 
        isOpen={isBrandKitOpen} 
        onClose={() => setIsBrandKitOpen(false)} 
      />

      <header className="py-6 flex items-center justify-between px-8 relative z-10 border-b border-white/5">
        <div className="flex items-center gap-3">
           <button 
            onClick={handleOpenKeySelector}
            className={`p-2.5 bg-gray-900 border border-gray-800 rounded-xl transition-all shadow-lg flex items-center gap-2 group ${hasApiKey ? 'hover:border-emerald-500/50' : 'hover:border-amber-500/50'}`}
            title="Manage API Key"
          >
            <KeyIcon className={`w-5 h-5 transition-colors ${hasApiKey ? 'text-emerald-500' : 'text-amber-500 group-hover:animate-pulse'}`} />
            <div className="hidden md:flex flex-col items-start leading-none">
              <span className="text-[8px] font-black uppercase tracking-widest text-gray-500">API Connection</span>
              <span className={`text-[10px] font-bold uppercase tracking-tighter ${hasApiKey ? 'text-emerald-400' : 'text-amber-400'}`}>
                {hasApiKey ? 'Ready' : 'Setup Required'}
              </span>
            </div>
          </button>
        </div>

        <div className="flex flex-col items-center gap-1">
          <h1 className="text-4xl font-black tracking-[0.2em] text-center bg-gradient-to-b from-white to-gray-600 bg-clip-text text-transparent uppercase">
            CutCalc
          </h1>
          <span className="text-[10px] font-bold text-[#D87431] uppercase tracking-[0.3em]">Influencer Engine</span>
        </div>

        <button 
          onClick={() => setIsBrandKitOpen(true)}
          className="p-2.5 bg-gray-900 border border-gray-800 rounded-xl hover:bg-gray-800 transition-colors shadow-lg"
          title="Open Brand Kit"
        >
          <div className="relative">
            <CurvedArrowDownIcon className="w-5 h-5 text-gray-400 rotate-180" />
            {brandConfig.isActive && <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#D87431] rounded-full" />}
          </div>
        </button>
      </header>

      <main className="w-full max-w-4xl mx-auto flex-grow flex flex-col p-4 relative">
        {appState === AppState.IDLE ? (
          <>
            <div className="flex-grow flex items-center justify-center">
              <div className="relative text-center">
                <h2 className="text-3xl font-extralight text-gray-700 tracking-tight italic">
                  {initialFormValues?.mode === GenerationMode.EXTEND_VIDEO ? 'Expanding the sequence...' : 'Design the vision...'}
                </h2>
                {!initialFormValues && <CurvedArrowDownIcon className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-16 h-16 text-gray-800 opacity-20" />}
              </div>
            </div>
            <div className="pb-8">
              <PromptForm
                onGenerate={handleGenerate}
                initialValues={initialFormValues}
                coreStyleActive={brandConfig.isActive}
              />
            </div>
          </>
        ) : (
          <div className="flex-grow flex items-center justify-center overflow-y-auto custom-scrollbar p-4">
            {appState === AppState.LOADING && <LoadingIndicator />}
            {appState === AppState.SUCCESS && videoUrl && (
              <VideoResult
                videoUrl={videoUrl}
                parentVideoUrl={parentVideoUrl}
                onRetry={handleRetry}
                onNewVideo={handleNewVideo}
                onExtend={handleExtend}
                canExtend={lastConfig?.resolution === Resolution.P720}
                aspectRatio={lastConfig?.aspectRatio || AspectRatio.LANDSCAPE}
              />
            )}
            {appState === AppState.ERROR && errorMessage && (
              <div className="text-center bg-red-950/20 border border-red-500/30 p-10 rounded-3xl max-w-md shadow-2xl backdrop-blur-md">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${isQuotaError ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
                  {isQuotaError ? (
                    <span className="text-2xl font-bold text-amber-500">⏳</span>
                  ) : (
                    <span className="text-2xl font-bold text-red-500">!</span>
                  )}
                </div>
                <h2 className="text-2xl font-black text-white mb-3 uppercase tracking-tighter">
                  {isQuotaError ? 'Quota Exceeded' : 'System Error'}
                </h2>
                <p className="text-sm text-gray-300 leading-relaxed mb-8">
                  {isQuotaError 
                    ? "You've reached your API rate limit. Please wait a few minutes or check your billing status in Google AI Studio."
                    : errorMessage}
                </p>
                
                {isQuotaError && (
                  <div className="flex flex-col gap-3 mb-8">
                    <a href="https://aistudio.google.com/app/plan_management" target="_blank" className="text-xs text-amber-400 underline font-bold uppercase tracking-widest">Check Quota Limits</a>
                    <a href="https://ai.google.dev/gemini-api/docs/billing" target="_blank" className="text-xs text-amber-400 underline font-bold uppercase tracking-widest">Enable Billing</a>
                  </div>
                )}

                <button onClick={handleNewVideo} className="w-full py-4 bg-white text-black rounded-xl font-black uppercase tracking-widest hover:bg-gray-200 transition-all active:scale-95 shadow-xl">Back to Console</button>
              </div>
            )}
          </div>
        )}
      </main>
      
      {brandConfig.isActive && appState === AppState.IDLE && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-[#D87431]/10 border border-[#D87431]/20 rounded-full flex items-center gap-3 backdrop-blur-md animate-pulse z-0">
           <div className="w-2 h-2 bg-[#D87431] rounded-full shadow-[0_0_8px_rgba(216,116,49,0.8)]" />
           <span className="text-[10px] font-bold text-[#D87431] uppercase tracking-widest">Brand Shield Engaged</span>
        </div>
      )}
    </div>
  );
};

export default App;
